/**
 * Inline plan panel for CreateExperiment Setup —
 * hypothesis + live power/duration readout (merges TestPlanner / TestPlanningModal).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { CalculatorIcon, InformationCircleIcon } from "@hugeicons/core-free-icons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { formatNumber } from "@/lib/utils/abTestCalculations"
import { TEST_TYPE_CONFIGS, estimateTestDuration, type TestType } from "@/lib/utils/abTestStatistics"
import { DEFAULT_PLAN, formatAbout } from "@/components/experiments/newTestModel"

type ExperimentPlanPanelProps = {
  hypothesis: string
  onHypothesisChange: (value: string) => void
  dailyVisitors?: number
  /** Where the daily figure came from, e.g. "/pricing, last 30 days" */
  dailyVisitorsSource?: string
  /** Baseline, MDE and significance the page picker estimated with, so both read the same duration */
  defaultPlan?: { baseline: number; mde: number; significance: number }
  /** Estimated days to a result, null when the inputs can't give one */
  onEstimateChange?: (days: number | null) => void
  variantCount?: number
  goalId?: string
  className?: string
}

function goalToTestType(goalId?: string): TestType {
  if (!goalId) return "conversion"
  if (goalId === "revenue") return "revenue"
  if (goalId === "page_funnel" || goalId === "external_page_funnel") return "funnel"
  if (goalId === "session_time" || goalId === "bounce_rate") return "engagement"
  return "conversion"
}

export function ExperimentPlanPanel({
  hypothesis,
  onHypothesisChange,
  dailyVisitors: dailyVisitorsProp,
  dailyVisitorsSource,
  defaultPlan = DEFAULT_PLAN,
  onEstimateChange,
  variantCount = 2,
  goalId,
  className,
}: ExperimentPlanPanelProps) {
  const testType = goalToTestType(goalId)
  const config = TEST_TYPE_CONFIGS[testType]
  const [baseline, setBaseline] = useState(String(defaultPlan.baseline))
  const [mde, setMde] = useState(String(defaultPlan.mde))
  const [significance, setSignificance] = useState(String(defaultPlan.significance))
  const [dailyVisitors, setDailyVisitors] = useState(dailyVisitorsProp ? String(dailyVisitorsProp) : "")
  // Page traffic arrives after mount; follow it until the user types their own figure.
  const dailyEdited = useRef(false)
  useEffect(() => {
    if (!dailyEdited.current && dailyVisitorsProp) setDailyVisitors(String(dailyVisitorsProp))
  }, [dailyVisitorsProp])

  // Same estimator as the page picker's "Time to a result", so the two agree.
  const estimate = useMemo(() => {
    const b = parseFloat(baseline)
    const m = parseFloat(mde)
    const s = parseFloat(significance)
    const d = parseFloat(dailyVisitors)
    if (!b || !m || !s || !d || b <= 0 || b >= 100 || d <= 0) return null
    return estimateTestDuration(d, b / 100, m, s, variantCount)
  }, [baseline, mde, significance, dailyVisitors, variantCount])

  useEffect(() => {
    onEstimateChange?.(estimate ? estimate.days : null)
  }, [estimate, onEstimateChange])

  return (
    <Card className={cn("border-border/30 gap-0", className)}>
      <CardHeader className="border-b border-border/40 pb-3">
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-lg bg-muted/40 border border-border/40 flex items-center justify-center">
            <HugeiconsIcon icon={CalculatorIcon} size={16} className="text-muted-foreground" />
          </div>
          <div>
            <CardTitle className="text-sm font-medium text-foreground">Plan</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Hypothesis and power: how long until you can call this test.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Hypothesis</Label>
          <Textarea
            value={hypothesis}
            onChange={(e) => onHypothesisChange(e.target.value)}
            placeholder="If we … then … because …"
            className="min-h-[68px] text-sm"
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <Field
            label="Daily visitors"
            tip={dailyVisitorsSource ? `Unique visitors a day: ${dailyVisitorsSource}` : "Unique visitors a day to the page under test"}
          >
            <Input
              type="number"
              value={dailyVisitors}
              onChange={(e) => { dailyEdited.current = true; setDailyVisitors(e.target.value) }}
              className="h-8 text-xs"
            />
          </Field>
          <Field label="Baseline %" tip="Current conversion rate">
            <Input
              type="number"
              value={baseline}
              onChange={(e) => setBaseline(e.target.value)}
              className="h-8 text-xs"
            />
          </Field>
          <Field label="Smallest lift %" tip="The smallest change worth detecting. Smaller lifts need more visitors">
            <Input
              type="number"
              value={mde}
              onChange={(e) => setMde(e.target.value)}
              className="h-8 text-xs"
            />
          </Field>
          <Field label="Significance" tip="Confidence threshold">
            <Select value={significance} onValueChange={setSignificance}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="90">90%</SelectItem>
                <SelectItem value="95">95%</SelectItem>
                <SelectItem value="99">99%</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="rounded-lg border border-border/30 bg-muted/15 px-3.5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <Readout
            label="Visitors per version"
            value={estimate ? formatNumber(estimate.requiredSamplePerVariant) : "–"}
          />
          <span className="w-px h-3 bg-border/30 hidden sm:block" />
          <Readout
            label="Time to a result"
            value={estimate ? formatAbout(estimate.days) : "–"}
            tone={estimate && estimate.days > 60 ? "warning" : undefined}
          />
          <span className="w-px h-3 bg-border/30 hidden sm:block" />
          <Readout
            label="Recommended"
            value={`${config.minDuration}–${config.maxDuration}d · ${config.significance}%`}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function Field({
  label,
  tip,
  children,
}: {
  label: string
  tip: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <Label className="text-xs text-muted-foreground">
          {label}
        </Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="text-muted-foreground/60">
                <HugeiconsIcon icon={InformationCircleIcon} size={11} />
              </button>
            </TooltipTrigger>
            <TooltipContent className="text-xs max-w-[200px]">{tip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {children}
    </div>
  )
}

function Readout({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: "warning"
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "text-sm font-medium tabular-nums",
          tone === "warning" ? "text-warning" : "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  )
}
